import type React from "react";
import { useEffect, useMemo, useRef, useState } from "react";

import { zodResolver } from "@hookform/resolvers/zod";
import { Divider, TextField } from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import type { DateValidationError } from "@mui/x-date-pickers/models";
import axios from "axios";
import { Controller, FormProvider, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import invariant from "ts-invariant";

import { translateError } from "api/errors/helper";
import { useGetManagerPatients } from "api/hooks/useGetManagerPatients";
import { useGetPatients } from "api/hooks/useGetPatients";
import type { Patients } from "api/schemas/Patients";
import {
  useDeleteAppointmentV2,
  useDeleteAppointmentV2Manager,
  useGetAppointmentsV2,
  useGetHcpServices,
  useGetManagerServices,
  usePostAppointmentV2,
  usePostAppointmentV2Manager,
  usePutAppointmentV2,
  usePutAppointmentV2Manager,
} from "api/services/appointment/hooks";
import type { AppointmentV2 } from "api/services/appointment/schemas/appointment_v2";
import {
  type BookingFormData,
  BookingFormManagerSchema,
  BookingFormSchema,
} from "api/services/appointment/schemas/booking_form";
import type { HcpService, HcpServiceManager } from "api/services/appointment/schemas/hcp_service";
import type { SelectedDate } from "contexts/CalendarContext";
import { useProfileContext } from "contexts/ProfileContext";
import { MUIDropdown, MUITextInput } from "shared/atoms/inputs";
import { MUIAutocompleteVirtualized } from "shared/atoms/MUIAutocompleteVirtualized";
import { Notification } from "shared/atoms/Notification";
import { MarketTypes } from "types";
import { useLocalizedDate } from "utils/date";
import { reportError } from "utils/errorReporting";
import { useActivityLogging } from "utils/hooks/useActivityLogging";
import { isUserMarket, userIsAdmin, userIsTherapist } from "utils/profile/profileHelper";

import { generateAvailabilityOptions } from "../../helpers";
import type { FormStates } from "../../helpers/misc";
import { AppointmentInterval } from "../AppointmentInterval";
import type { SetSelectArgs } from "../Calendar/Calendar";
import {
  convertDateStringToObject,
  formDateToJSDates,
  generateOptions,
  generateParticipantOptionsFull,
} from "../helpers";

import { FormFooter } from "./FormFooter";
import { getDefaultDate } from "./getDefaultDate";
import { hasConflict } from "./hasConflict";
import { Container, Input, Wrapper } from "./styles";

export type Service = HcpService | HcpServiceManager;

interface Props {
  defaultServiceID: number | undefined;
  event?: AppointmentV2 | null;
  selectedDate?: SelectedDate | null;
  selectedPatient?: string | null;
  onClose: (booked?: boolean) => void;
  refetchAppointments?: () => void;
  setSelect?: (args: SetSelectArgs) => void;
}

export const BookAppointmentForm: React.FC<Props> = ({
  defaultServiceID,
  event,
  selectedDate,
  selectedPatient,
  onClose,
  refetchAppointments,
  setSelect,
}) => {
  const { profile } = useProfileContext();
  const { t } = useTranslation();
  const { format, isValid: isValidDate } = useLocalizedDate();
  const [services, setServices] = useState<Service[]>([]);
  const [editMode] = useState(!!event);
  const [bookingWarning, setBookingWarning] = useState("");
  const { trackActivity } = useActivityLogging();
  const startTime = useRef(new Date().toISOString());

  invariant(profile);
  const isAdmin = userIsAdmin(profile);
  const isTherapist = userIsTherapist(profile);
  const isUS = isUserMarket(MarketTypes.US, profile);

  const { data: therapistPatients } = useGetPatients({ userID: profile.id }, { enabled: isTherapist });
  const { data: adminPatients } = useGetManagerPatients(profile.id, { enabled: isAdmin });
  const allPatients = [...(therapistPatients || []), ...(adminPatients || [])]; // a pt can be both pt and admin
  const patients = allPatients.filter((patient, index, array) => array.findIndex(p => p.id === patient.id) === index); // remove duplicates

  const { data: hcpServices = [] } = useGetHcpServices(profile.id, {
    enabled: !isAdmin,
  });
  const { data: servicesManager = [] } = useGetManagerServices(profile.id, {
    enabled: isAdmin,
  });

  useEffect(() => {
    if (!isAdmin && hcpServices.length > 0) {
      setServices(hcpServices);
    } else if (isAdmin && servicesManager.length > 0) {
      setServices(servicesManager);
    }
  }, [isAdmin, hcpServices, servicesManager]);

  // API - health_care_professionals
  const postAppointmentMutation = usePostAppointmentV2();
  const putAppointmentMutation = usePutAppointmentV2();
  const deleteAppointmentMutation = useDeleteAppointmentV2();

  // API - manager
  const postAppointmentManagerMutation = usePostAppointmentV2Manager();
  const putAppointmentManagerMutation = usePutAppointmentV2Manager();
  const deleteAppointmentManagerMutation = useDeleteAppointmentV2Manager();

  const [selectedStartTime, setSelectedStartTime] = useState<number | undefined>(undefined);
  const [selectedEndTime, setSelectedEndTime] = useState<number | undefined>(undefined);
  const [formState, setFormState] = useState<FormStates>("default");
  const [error, setError] = useState<string | null>(null);
  const [duration, setDuration] = useState<number | undefined>(undefined);
  const [personnelOptions, setPersonnelOptions] = useState(
    isTherapist ? [{ label: `${profile?.first_name} ${profile?.last_name}`, value: profile.id }] : []
  );

  const [dateError, setDateError] = useState<DateValidationError | null>(null);

  const errorMessage = useMemo(() => {
    switch (dateError) {
      case "disablePast": {
        return t("errors.past_date");
      }

      case "invalidDate": {
        return t("errors.invalid_date");
      }

      default: {
        return "";
      }
    }
  }, [dateError]);

  const getDefaultPatientID = () => {
    if (selectedPatient) {
      return selectedPatient;
    }
    return event?.participants?.[0]?.id.toString() ? event?.participants?.[0]?.id.toString() : undefined;
  };

  const form = useForm<BookingFormData>({
    mode: "onSubmit",
    reValidateMode: "onChange",
    resolver: zodResolver(isAdmin ? BookingFormManagerSchema : BookingFormSchema),
    defaultValues: {
      date: getDefaultDate(event?.start_time),
      health_care_provider_service_id: event?.health_care_provider_service_id?.toString() ?? "",
      medium: event?.medium ?? "",
      note: event?.note ?? "",
      patient_id: getDefaultPatientID(),
      personnel: event?.health_care_professional_id ?? personnelOptions[0]?.value,
      start_time: event?.start_time ? convertDateStringToObject(event.start_time).startTime : undefined,
      end_time: event?.end_time ? convertDateStringToObject(event.end_time).startTime : undefined,
    },
  });
  const {
    control,
    handleSubmit,
    setValue,
    getValues,
    watch,
    formState: { errors },
  } = form;

  const selectedPersonnel = watch("personnel");
  const selectedService = watch("health_care_provider_service_id");
  const hcpService = services.find(service => service.id === Number(selectedService));
  const watchDate = watch("date");
  const watchStartTime = watch("start_time");
  const watchEndTime = watch("end_time");
  const selectedFormPatient = patients.find(patient => patient.id === Number(watch("patient_id")));

  const hasCeStructure = (patient: typeof selectedFormPatient): patient is Patients => {
    return patient !== undefined && "ce_structure" in patient;
  };

  const filteredServices = useMemo(() => {
    if (hasCeStructure(selectedFormPatient) && selectedFormPatient.ce_structure === "hybrid") {
      return services.filter(service => service.purpose === "hybrid_call");
    }
    return services;
  }, [services, selectedFormPatient]);

  // set default value for services dropdown
  useEffect(() => {
    if (hasCeStructure(selectedFormPatient) && selectedFormPatient.ce_structure === "hybrid") {
      const hybridService = filteredServices.find(service => service.purpose === "hybrid_call");
      if (hybridService) {
        setValue("health_care_provider_service_id", String(hybridService.id));
        return;
      }
    }

    if (!selectedService && defaultServiceID) {
      setValue("health_care_provider_service_id", String(defaultServiceID));
    } else if (!selectedService && filteredServices.length > 0) {
      setValue("health_care_provider_service_id", String(filteredServices[0].id));
    }
  }, [defaultServiceID, filteredServices, selectedService, selectedFormPatient, setValue]);

  const { data: appointmentsData, isSuccess } = useGetAppointmentsV2(
    {
      userId: profile.id,
      startDate: encodeURIComponent(
        isValidDate(new Date(watchDate)) ? new Date(watchDate).toISOString() : new Date().toISOString()
      ),
      view: "day",
      isAdmin,
    },
    { enabled: !!watchDate && isValidDate(new Date(watchDate)) }
  );

  const appointments = appointmentsData?.appointments ?? [];
  const bookingIntervals = appointmentsData?.booking_intervals ?? [];

  useEffect(() => {
    if (watchDate && isValidDate(new Date(watchDate))) {
      if (setSelect) {
        const { startDate, endDate } = formDateToJSDates({
          date: watchDate,
          startTime: watchStartTime ?? 0,
          endTime: watchEndTime ?? 0,
        });
        setSelect({ start: startDate, end: endDate });
      }

      if (isSuccess && watchStartTime && watchEndTime && selectedPersonnel !== undefined) {
        const conflict = hasConflict({
          date: watchDate,
          startTime: watchStartTime,
          endTime: watchEndTime,
          appointments,
          bookingIntervals,
          personnelOptions,
          selectedAppointment: event,
          selectedPersonnel,
          userID: profile.id,
        });

        setBookingWarning(conflict);
      }
    }
  }, [selectedPersonnel, watchDate, watchStartTime, watchEndTime]);

  useEffect(() => {
    const dateStart = selectedDate && convertDateStringToObject(selectedDate.startStr);
    const dateEnd = selectedDate && convertDateStringToObject(selectedDate.endStr);

    if (dateStart?.startTime) {
      setSelectedStartTime(dateStart.startTime);
    }
    if (dateEnd?.startTime) {
      setSelectedEndTime(dateEnd.startTime);
    }
    if (dateStart?.date) {
      setValue("date", dateStart.date);
    }
  }, [selectedDate]);

  useEffect(() => {
    if (!hcpService) {
      return;
    }

    // Set default duration to service's duration
    setDuration(hcpService?.duration);

    // Set default medium based on service's mode
    if (hcpService?.mode === "digital") {
      setValue("medium", "video");
    } else if (hcpService?.mode === "physical") {
      setValue("medium", "face_to_face");
    }

    // Set personnel options based on service's availble health_care_professionals
    if ("health_care_professionals" in hcpService && hcpService.health_care_professionals) {
      setPersonnelOptions(
        hcpService.health_care_professionals.map(professional => ({
          label: `${professional?.full_name}`,
          value: professional.id,
        }))
      );
    }
  }, [hcpService]);

  const serviceOptions = generateOptions({ array: filteredServices, labelKey: "name", valueKey: "id" });

  const mediumOptions =
    hcpService?.mode === "digital"
      ? [
          { label: t("booking.form.medium.phone"), value: "phone" },
          { label: t("booking.form.medium.video"), value: "video" },
        ]
      : [{ label: t("booking.form.medium.face_to_face"), value: "face_to_face" }];

  const participantOptionsFull = generateParticipantOptionsFull(patients);

  const onSetupCallback = (track?: boolean, patientId?: number) => {
    return {
      onSuccess: () => {
        if (isTherapist && isUS && track && patientId) {
          trackActivity({
            category: "meeting_booked_with_patient",
            patient_id: patientId,
            start_time: startTime.current,
            end_time: new Date().toISOString(),
          });
        }

        if (refetchAppointments) refetchAppointments();
        setFormState("success");
        if (onClose) setTimeout(() => onClose(true), 3000);
      },
      onError(e: unknown) {
        if (axios.isAxiosError(e)) {
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          // @ts-ignore
          setError(t(translateError(e.response?.data.error_code)));
        }
        setFormState("default");
        if (e instanceof Error || typeof e === "string") {
          reportError("BookingForm.tsx", e);
        }
      },
    };
  };

  const onSubmit = handleSubmit(formData => {
    const { startDate, endDate } = formDateToJSDates({
      date: formData.date,
      startTime: formData.start_time,
      endTime: formData.end_time,
      ianaTimeZone: profile.iana_timezone,
    });
    const startDateISOStr = startDate.toISOString();
    const endDateISOStr = endDate.toISOString();

    setFormState("saving");
    setError(null);

    const data = {
      end_time: endDateISOStr,
      health_care_provider_service_id: parseInt(formData.health_care_provider_service_id, 10),
      medium: formData.medium,
      note: formData.note,
      patient_id: parseInt(formData.patient_id, 10),
      start_time: startDateISOStr,
    };
    if (editMode && event) {
      putAppointmentMutation.mutateAsync(
        {
          data,
          hcProfId: profile.id,
          appointmentId: event.id,
        },
        onSetupCallback()
      );
    } else {
      postAppointmentMutation.mutateAsync(
        {
          data,
          hcProfId: profile.id,
        },
        onSetupCallback(true, parseInt(formData.patient_id, 10))
      );
    }
  });

  const onSubmitManager = handleSubmit(formData => {
    const { startDate, endDate } = formDateToJSDates({
      date: formData.date,
      startTime: formData.start_time,
      endTime: formData.end_time,
      ianaTimeZone: profile.iana_timezone,
    });
    const startDateISOStr = startDate.toISOString();
    const endDateISOStr = endDate.toISOString();

    if (formData.personnel === undefined) return;

    setFormState("saving");
    setError(null);

    const data = {
      end_time: endDateISOStr,
      health_care_professional_id: formData.personnel,
      health_care_provider_service_id: parseInt(formData.health_care_provider_service_id, 10),
      medium: formData.medium,
      note: formData.note,
      patient_id: parseInt(formData.patient_id, 10),
      start_time: startDateISOStr,
    };
    if (editMode && event) {
      putAppointmentManagerMutation.mutateAsync(
        {
          data,
          managerId: profile.id,
          appointmentId: event.id,
        },
        onSetupCallback()
      );
    } else {
      postAppointmentManagerMutation.mutateAsync(
        {
          data,
          managerId: profile.id,
        },
        onSetupCallback()
      );
    }
  });

  const deleteAppointment = () => {
    if (event) {
      if (isAdmin) {
        deleteAppointmentManagerMutation.mutateAsync(
          { managerId: profile.id, appointmentId: event.id },
          onSetupCallback()
        );
      } else {
        deleteAppointmentMutation.mutateAsync({ hcProfId: profile.id, appointmentId: event.id }, onSetupCallback());
      }
    }
  };

  const renderContent = () => {
    return (
      <FormProvider {...form}>
        <form onSubmit={isAdmin ? onSubmitManager : onSubmit} data-testid="booking-form">
          <Input>
            <MUIDropdown
              label={t("booking.form.service")}
              name="health_care_provider_service_id"
              options={serviceOptions}
              disabled={editMode}
              required
              error={errors.health_care_provider_service_id && t("errors.field.required")}
            />
          </Input>

          <Divider sx={{ mb: 2 }} />

          <Input>
            <MUIDropdown
              label={t("booking.form.mode")}
              name="medium"
              options={mediumOptions ?? []}
              disabled={!selectedService}
              required
              error={errors.medium && t("errors.field.required")}
            />
          </Input>

          <Input className="date-and-time">
            <Controller
              name="date"
              control={control}
              render={({ field: { onChange, value } }) => (
                <Wrapper>
                  <DatePicker
                    name="date"
                    format="yyyy-MM-dd"
                    value={value ? new Date(`${value}T00:00:00`) : null}
                    onChange={input => {
                      if (input && isValidDate(input)) {
                        onChange(format(input, "yyyy-MM-dd"));
                      } else {
                        onChange(input);
                      }
                    }}
                    onError={newError => setDateError(newError)}
                    slotProps={{
                      textField: {
                        helperText: errorMessage,
                      },
                    }}
                    disablePast
                  />
                </Wrapper>
              )}
            />
          </Input>

          <Input>
            <AppointmentInterval
              options={generateAvailabilityOptions(hcpService as HcpService, 5)}
              setValue={setValue}
              getValues={getValues}
              duration={duration}
              selectedStartTime={selectedStartTime}
              selectedEndTime={selectedEndTime}
              errors={errors}
            />
          </Input>

          <Input>
            <MUIDropdown
              label={t("booking.form.personnel")}
              name="personnel"
              options={personnelOptions ?? []}
              disabled={!isAdmin || Boolean(selectedPatient)}
              error={errors.personnel && t("errors.field.required")}
            />
          </Input>

          <Input>
            <MUIAutocompleteVirtualized
              name="patient_id"
              renderInput={params => (
                <TextField {...params} error={!!error} variant="outlined" label={t("booking.form.participants")} />
              )}
              options={participantOptionsFull}
              disableClearable
              disabled={editMode || Boolean(selectedPatient)}
              error={errors.patient_id && t("errors.field.required")}
            />
          </Input>

          <Input>
            <MUITextInput label={t("booking.form.note")} multiline name="note" />
          </Input>

          {bookingWarning && <Notification variant="warning">{bookingWarning}</Notification>}

          <FormFooter
            formState={formState}
            editMode={editMode}
            event={event}
            error={error}
            deleteAppointment={deleteAppointment}
          />
        </form>
      </FormProvider>
    );
  };

  return <Container>{renderContent()}</Container>;
};
